import { useMemo, useState } from 'react'
import { useList } from '@refinedev/core'
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
} from '@mui/material'
import { dataProvider } from '../providers/dataProvider'
import TestFiltersOpportunitiesList from './TestFiltersOpportunitiesList'
import { getAsciiName } from '../utils/textUtils'
import { IwiData } from '@/interfaces/iwi'

export default function TestFilters() {
  const [amount, setAmount] = useState<number>(0)
  const [region, setRegion] = useState<number | undefined>(undefined)
  const [fundingRequiredBy, setFundingRequiredBy] = useState<
    string | undefined
  >(undefined)
  // Hard filter states
  const [isRegistered, setIsRegistered] = useState<boolean | undefined>(
    undefined
  )
  const [acceptsGamblingProceeds, setAcceptsGamblingProceeds] = useState<
    boolean | undefined
  >(undefined)
  const [iwi, setIwi] = useState<IwiData[] | undefined>(undefined)
  const [acceptsInternational, setAcceptsInternational] = useState<
    boolean | undefined
  >(undefined)
  const [onlyFundsRegisteredCharities, setOnlyFundsRegisteredCharities] =
    useState<boolean | undefined>(undefined)
  const [includeRelational, setIncludeRelational] = useState<boolean>(true)

  const [filteredOpportunitiesUUID, setFilteredOpportunitiesUUID] = useState<
    any[]
  >([])

  const [filtering, setFiltering] = useState(false)
  const [filtersApplied, setFiltersApplied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: regionOptions } = useList({
    resource: 'regions',
    meta: {
      paginate: false,
    },
    sorters: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  })

  const { data: iwiData } = useList({
    resource: 'iwi',
    config: {
      pagination: { current: 1, pageSize: 200 },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting iwi list',
        description:
          error?.message || 'An error occurred while fetching iwi list',
        type: 'error',
      }
    },
  })

  const iwiList = useMemo(
    () => (iwiData?.data as IwiData[]) || [],
    [iwiData?.data]
  )

  async function applyFilters(
    amount: number,
    region: number | undefined,
    fundingRequiredBy: string | undefined,
    isRegistered: boolean | undefined,
    acceptsGamblingProceeds: boolean | undefined,
    acceptsInternational: boolean | undefined,
    iwi: IwiData[] | undefined,
    onlyFundsRegisteredCharities: boolean | undefined,
    includeRelational: boolean
  ) {
    // Check if any filters are applied
    if (
      amount === 0 &&
      region === undefined &&
      fundingRequiredBy === undefined &&
      isRegistered === undefined &&
      acceptsGamblingProceeds === undefined &&
      acceptsInternational === undefined &&
      (iwi === undefined || iwi.length === 0) &&
      onlyFundsRegisteredCharities === undefined
    ) {
      return
    }
    setFiltering(true)
    setFiltersApplied(true)

    try {
      const meta: any = {
        testFilters: true,
        amount: amount || undefined,
        region: region,
        funding_required_by: fundingRequiredBy,
        include_relational: includeRelational,
      }

      // Add hard filter parameters only if they have values
      if (isRegistered !== undefined) {
        meta.is_registered = isRegistered
      }
      if (acceptsGamblingProceeds !== undefined) {
        meta.accepts_gambling_proceeds = acceptsGamblingProceeds
      }
      if (acceptsInternational !== undefined) {
        meta.accepts_international = acceptsInternational
      }
      if (iwi !== undefined && iwi.length > 0) {
        meta.iwi = iwi.map((i) => i.id)
      }
      if (onlyFundsRegisteredCharities !== undefined) {
        meta.only_funds_registered_charities = onlyFundsRegisteredCharities
      }

      const response = await dataProvider.getList({
        resource: 'opportunities',
        meta,
      })
      setError(null)
      setFilteredOpportunitiesUUID(response.data)
      setFiltering(false)
      setFiltersApplied(true)
    } catch (error) {
      setError('Error applying filters')
      setFiltering(false)
      setFiltersApplied(false)
    }
  }

  function handleSubmit() {
    applyFilters(
      amount,
      region,
      fundingRequiredBy,
      isRegistered,
      acceptsGamblingProceeds,
      acceptsInternational,
      iwi,
      onlyFundsRegisteredCharities,
      includeRelational
    )
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5">Filter Opportunities</Typography>
        <Typography variant="h6" sx={{ mt: 3 }}>
          About this tool
        </Typography>
        <Typography variant="body1" sx={{}}>
          Input an amount, location and/or date when funding is required by to
          to see which opportunities pass particular filters during matching.
        </Typography>
        {/* Amount Filter */}
        <TextField
          label="Max Amount ($)"
          InputLabelProps={{ shrink: true }}
          type="number"
          fullWidth
          value={amount === 0 ? '' : amount}
          placeholder="Enter amount"
          onChange={(e) => setAmount(Number(e.target.value))}
          sx={{ mt: 3 }}
        />
        {/* Location Filter */}
        <Autocomplete
          value={
            region
              ? regionOptions?.data?.find((opt) => opt.id === region) || null
              : null
          }
          onChange={(event, value) => setRegion(value?.id ?? undefined)}
          options={regionOptions?.data || []}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, params) => {
            const inputValue = params.inputValue.toLowerCase().trim()
            if (!inputValue) return []
            const asciiInput = getAsciiName(inputValue)

            const filtered = options.filter((option) => {
              // Check main name
              if (option.ascii_name) {
                if (option.ascii_name.toLowerCase().includes(asciiInput)) {
                  return true
                }
              } else if (getAsciiName(option.name).includes(asciiInput)) {
                return true
              }

              // Check additional_names if they exist
              if (
                option.additional_names &&
                option.additional_names.length > 0
              ) {
                return option.additional_names.some((additionalName: string) =>
                  getAsciiName(additionalName)
                    .toLowerCase()
                    .includes(asciiInput)
                )
              }

              return false
            })
            return filtered
          }}
          renderOption={(props, option) => (
            <li {...props}>
              {option.name}
              {option.additional_names &&
                option.additional_names.length > 0 && (
                  <span style={{ color: 'gray', marginLeft: '8px' }}>
                    - {option.additional_names.join(', ')}
                  </span>
                )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Location"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              fullWidth
              placeholder="Enter location"
              sx={{ mt: 3 }}
            />
          )}
        />
        {/* Timeframe Filter */}
        <TextField
          label="Funding Required By"
          InputLabelProps={{ shrink: true }}
          type="date"
          fullWidth
          value={fundingRequiredBy}
          onChange={(e) => setFundingRequiredBy(e.target.value)}
          sx={{ mt: 3 }}
        />

        {/* Hard Filters Section */}
        <Divider sx={{ mt: 4, mb: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>
          Simulate Organisation Profile
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Configure this form as if you were an organisation setting up your
          profile. This will test how the hard filters work for organisations
          with different characteristics.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Enable Organisation Profile Testing */}
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  isRegistered !== undefined ||
                  acceptsGamblingProceeds !== undefined ||
                  iwi !== undefined ||
                  acceptsInternational !== undefined
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    // Set default org profile when enabled
                    setIsRegistered(true)
                    setAcceptsGamblingProceeds(false)
                    setAcceptsInternational(false)
                    setIwi(undefined)
                  } else {
                    // Clear org profile when disabled
                    setIsRegistered(undefined)
                    setAcceptsGamblingProceeds(undefined)
                    setAcceptsInternational(undefined)
                    setIwi(undefined)
                  }
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Apply Organisation Hard Filters
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Check this to simulate an organisation's funding preferences
                </Typography>
              </Box>
            }
          />

          {/* Organisation Settings - only show when enabled */}
          {(isRegistered !== undefined ||
            acceptsGamblingProceeds !== undefined ||
            iwi !== undefined ||
            acceptsInternational !== undefined) && (
            <Box
              sx={{
                ml: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Organisation Characteristics:
              </Typography>

              {/* Is Registered Charity */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRegistered === true}
                    onChange={(e) => setIsRegistered(e.target.checked)}
                  />
                }
                label="We are a registered charity"
              />

              {/* Accepts Gambling Proceeds */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptsGamblingProceeds === true}
                    onChange={(e) =>
                      setAcceptsGamblingProceeds(e.target.checked)
                    }
                  />
                }
                label="We accept funding from gambling proceeds"
              />
              {/* Accepts International Funding */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptsInternational === true}
                    onChange={(e) => setAcceptsInternational(e.target.checked)}
                  />
                }
                label="We accept funding from international sources"
              />

              <Autocomplete
                multiple
                value={iwi}
                sx={{ mb: 1, mt: 2 }}
                onChange={(_event, newValues) => {
                  setIwi(newValues)
                }}
                options={iwiList}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, params) => {
                  const inputValue = params.inputValue?.toLowerCase().trim()
                  if (!inputValue) return []
                  const asciiInput = getAsciiName(inputValue)

                  return options.filter((option) => {
                    return getAsciiName(option.name).includes(asciiInput)
                  })
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    label="Iwi"
                    {...params}
                    placeholder="Search iwi"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: { backgroundColor: '#fff' },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* Include Relational Opportunities */}
          <FormControlLabel
            control={
              <Checkbox
                checked={includeRelational}
                onChange={(e) => setIncludeRelational(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Include Relational Funding Opportunities
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Include opportunities that require building a relationship
                  with the funder
                </Typography>
              </Box>
            }
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
          style={{ marginTop: '20px' }}
        >
          Apply Filters
        </Button>
        {error && <Typography sx={{ mt: 2, color: 'red' }}>{error}</Typography>}
        {filtering && (
          <Typography
            sx={{ my: 10, display: 'flex', justifyContent: 'center' }}
          >
            <CircularProgress />
          </Typography>
        )}
        {filteredOpportunitiesUUID && filteredOpportunitiesUUID.length > 0 && (
          <TestFiltersOpportunitiesList
            filteredOpportunitiesUUID={filteredOpportunitiesUUID}
          />
        )}
        {filtersApplied &&
          filteredOpportunitiesUUID &&
          filteredOpportunitiesUUID.length === 0 &&
          (amount || region || fundingRequiredBy) && (
            <>
              <Typography
                sx={{ my: 10, display: 'flex', justifyContent: 'center' }}
              >
                No Opportunities Matched the Filters
              </Typography>
            </>
          )}
      </CardContent>
    </Card>
  )
}
